import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const ALLOWED_ROUTES_MAINTENANCE = ['/auth', '/auth/callback', '/auth/bpm', '/standings', '/maintenance-build'];

function isAllowed(pathname: string): boolean {
  return ALLOWED_ROUTES_MAINTENANCE.some((r) => pathname.startsWith(r));
}

/**
 * When maintenance is active, redirects the user to /standings and blocks
 * browser back-button navigation to non-allowed pages.
 *
 * Must be rendered inside a <Router>.
 */
export function MaintenanceGuard({ active }: { active: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect to /standings if on a blocked route
  useEffect(() => {
    if (!active) return;
    if (!isAllowed(location.pathname)) {
      navigate('/standings', { replace: true });
    }
  }, [active, location.pathname, navigate]);

  // Block browser back button from leaving /standings during maintenance
  useEffect(() => {
    if (!active) return;

    // Push a dummy state so pressing back pops it instead of leaving
    const handlePopState = () => {
      if (!isAllowed(window.location.pathname)) {
        // User navigated back to a blocked route — push them forward
        navigate('/standings', { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [active, navigate]);

  return null;
}
